/**
 * The system map (docs/systems.md "Integrations"), drawn in the Now map's language: systems as cards in columns by
 * the direction data flows, integrations as thin orthogonal lines with short rounded corners and their label on the
 * line (the process and its runs), failing red, quiet dashed, thicker with more runs. Laid out for the width it has
 * by lib/systemlayout.ts (no zoom: it fills its container, and scrolls sideways only when the columns do not fit);
 * plain SVG under HTML cards and labels. Pointing at a line, a label or a card lights what it touches and shows the
 * processes; clicking a line selects it (the page keeps the selection in the URL), clicking a system opens it.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { Integration } from '../lib/api.ts';
import { ago, count } from '../lib/format.ts';
import { cadenceLabel } from '../lib/integrations.ts';
import { LABEL_H, layoutSystems, pairTitle, type SysEdge, type SysNode } from '../lib/systemlayout.ts';
import { systemColor, systemIcon, systemLogo, systemName } from '../lib/systems.ts';
import { measure } from '../map/measure.ts';
import { IconButton, Menu, cx, noun } from '../design/primitives.tsx';
import { SystemMark } from './SystemEditor.tsx';
import { CloseIcon, SearchIcon } from './icons.tsx';
import '../map/map.css';
import './systemmap.css';
const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
const STATUS_TEXT: Record<Integration['status'], string> = { ok: 'Running', failing: 'Failing', quiet: 'Quiet' };

/** The process name as a menu: search its data (leaves Settings), and what else the caller adds. */
export function ProcessMenu({ process, onSearch, children, className, extra }: {
  process: string; onSearch: (process: string) => void; children: ReactNode; className?: string; extra?: ReactNode;
}) {
  return (
    <Menu.Root>
      <Menu.Trigger className={cx('min-w-0 truncate text-left bg-transparent border-0 p-0 cursor-pointer text-inherit font-[inherit] hover:underline focus-visible:outline-2 focus-visible:outline-accent rounded-[3px]', className)}
        title={process} aria-label={`Actions for ${process}`}>
        {children}
      </Menu.Trigger>
      <Menu.Content align="start" className="min-w-64">
        <Menu.Label>{process}</Menu.Label>
        {extra}
        <Menu.Separator />
        <Menu.Label>Leaves Settings</Menu.Label>
        <Menu.Item icon={<SearchIcon size={16} />} onSelect={() => onSearch(process)}>Search the data of this process</Menu.Item>
      </Menu.Content>
    </Menu.Root>
  );
}

export function StatusDot({ status }: { status: Integration['status'] }) {
  return <span aria-label={STATUS_TEXT[status]} className={cx('inline-block size-2 rounded-full shrink-0', status === 'failing' ? 'bg-fail' : status === 'quiet' ? 'bg-warn' : 'bg-ok')} />;
}

const TONES = ['quiet', 'lit', 'fail'] as const;
const PEEK_W = 288;

/** Keeps --mv-sbw at the width of the page's scrollbar, so a block can reach the full width of the window (systemmap.css .sys-wide). */
let sbwWatched = false;
function watchScrollbar() {
  if (sbwWatched || typeof window === 'undefined') return;
  sbwWatched = true;
  const set = () => document.documentElement.style.setProperty('--mv-sbw', `${Math.max(0, window.innerWidth - document.documentElement.clientWidth)}px`);
  set();
  window.addEventListener('resize', set);
  new ResizeObserver(set).observe(document.documentElement);
}

export function SystemMap({ integrations, selected, onSelect, onOpenSystem, onSearch, onOpenProcess, windowLabel = 'last 7 days', openWhat = 'its card', maxHeight, bleed = false }: {
  integrations: readonly Integration[];
  /** The selected arc ("ERP>WMS"), or null. */
  selected: string | null;
  onSelect: (arc: string | null) => void;
  onOpenSystem: (code: string) => void;
  onSearch: (process: string) => void;
  /** The Systems view: a process under the map opens its page instead of the Settings menu. */
  onOpenProcess?: (process: string) => void;
  /** The window the runs are of, in the legend and the empty text ("last 7 days"). */
  windowLabel?: string;
  /** What clicking a system opens, in its tooltip ("its card"). */
  openWhat?: string;
  /** A cap on the height (the map scrolls within it); by default the map is as tall as it draws. */
  maxHeight?: string;
  /** The drawing reaches past a capped page column to the full width of the window (the Systems pages). */
  bleed?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [fontsReady, setFontsReady] = useState(false);
  const [hover, setHoverNow] = useState<string | null>(null);
  // The processes beside a line stay while the pointer moves from the line into them (and can be clicked there)
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setHover = (key: string | null) => {
    if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
    if (key) setHoverNow(key);
    else hideTimer.current = setTimeout(() => { hideTimer.current = null; setHoverNow(null); }, 280);
  };
  const keepHover = () => { if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; } };
  useEffect(() => () => { if (hideTimer.current) clearTimeout(hideTimer.current); }, []);
  const [hoverNode, setHoverNode] = useState<string | null>(null);
  useEffect(watchScrollbar, []);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.floor(e!.contentRect.width)));
    ro.observe(el);
    setWidth(Math.floor(el.getBoundingClientRect().width));
    return () => ro.disconnect();
  }, []);
  useEffect(() => { document.fonts?.ready.then(() => setFontsReady(true)).catch(() => undefined); }, []);
  const map = useMemo(
    () => (width > 0 ? layoutSystems(integrations, { width, measure, name: systemName }) : null),
    [integrations, width, fontsReady], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const focus = hover ?? selected;
  const lit = useMemo(() => {
    if (!map) return null;
    if (focus) return new Set([focus]);
    if (hoverNode) return new Set(map.edges.filter((e) => e.from === hoverNode || e.to === hoverNode).map((e) => e.key));
    return null;
  }, [map, focus, hoverNode]);
  const litNodes = useMemo(() => {
    if (!lit || !map) return null;
    const s = new Set<string>(hoverNode ? [hoverNode] : []);
    for (const e of map.edges) if (lit.has(e.key)) { s.add(e.from); s.add(e.to); }
    return s;
  }, [lit, map, hoverNode]);
  const edgeOpacity = (e: SysEdge) => (!lit || lit.has(e.key) ? 1 : e.status === 'failing' ? 0.9 : 0.18);
  const sel = map?.edges.find((a) => a.key === selected) ?? null;
  const hov = map && hover && hover !== selected ? map.edges.find((a) => a.key === hover) ?? null : null;
  const pick = (key: string) => onSelect(key === selected ? null : key);

  if (integrations.length === 0 || (map && map.nodes.length === 0)) {
    return <p className="m-0 p-10 text-center text-small text-n8">No integrations between systems in the {windowLabel}.</p>;
  }
  return (
    <div className="flex flex-col gap-3 min-w-0">
      <div className="sysm-legend">
        <span><svg width="30" height="10" aria-hidden="true"><path d="M1 5h26" className="sysm-key" /><path d="M22.5 1.5 27 5l-4.5 3.5" className="sysm-key-head" /></svg>Data flows this way</span>
        <span><svg width="30" height="10" aria-hidden="true"><path d="M1 5h28" className="sysm-key fail" /></svg>Failing</span>
        <span><svg width="30" height="10" aria-hidden="true"><path d="M1 5h28" className="sysm-key" strokeDasharray="5 4" /></svg>Quiet (overdue)</span>
        <span>Thicker = more runs · {windowLabel}</span>
      </div>
      <div ref={box} className={cx('sysm-box', bleed && 'sys-wide')} style={{ maxHeight, overflowY: maxHeight ? 'auto' : undefined }}>
        {!map ? <div className="mv-shimmer rounded-card h-[360px]" aria-hidden="true" /> : (
          <div className="relative" data-system-map="" style={{ width: map.width, height: map.height }}
            onClick={(e) => { if (selected && (e.target as Element).closest?.('[data-map-system],[data-map-arc],.sysm-hit,[role="dialog"]') === null) onSelect(null); }}>
            <svg className="absolute inset-0 block" width={map.width} height={map.height} aria-hidden="true">
              <defs>
                {TONES.map((t) => (
                  <marker key={t} id={`sysm-ah-${t}`} viewBox="0 0 10 10" refX="8.5" refY="5" markerWidth="9" markerHeight="9" markerUnits="userSpaceOnUse" orient="auto">
                    <path className={cx('mvm-arrow', t !== 'quiet' && t)} d="M1.5,1.5 L8.5,5 L1.5,8.5" />
                  </marker>
                ))}
              </defs>
              {map.edges.map((e) => {
                const on = !!lit?.has(e.key);
                const fail = e.status === 'failing';
                return (
                  <g key={e.key} className={cx('mvm-flow', fail && 'fail', on && 'lit')} style={{ opacity: edgeOpacity(e) }}>
                    <path className="mvm-line" d={e.d} strokeWidth={e.stroke + (on && selected === e.key ? 0.75 : 0)} strokeDasharray={e.status === 'quiet' ? '5 4' : undefined}
                      markerEnd={`url(#sysm-ah-${fail ? 'fail' : on ? 'lit' : 'quiet'})`} />
                  </g>
                );
              })}
              {map.edges.map((e) => (
                <path key={`hit-${e.key}`} className="sysm-hit" d={e.d}
                  onPointerEnter={() => setHover(e.key)} onPointerLeave={() => setHover(null)} onClick={(ev) => { ev.stopPropagation(); pick(e.key); }} />
              ))}
            </svg>
            {map.captions.map((c) => <span key={c.text} className="sysm-caption" style={{ left: c.x, top: c.y }}>{c.text}</span>)}
            {map.nodes.map((n) => (
              <NodeCard key={n.code} n={n} lone={map.lone.get(n.code) ?? []} openWhat={openWhat} dim={!!litNodes && !litNodes.has(n.code)}
                onOpen={() => onOpenSystem(n.code)} onHover={(on) => setHoverNode(on ? n.code : null)} />
            ))}
            {map.edges.map((e) => {
              const fail = e.status === 'failing';
              const l = e.label;
              return (
                <button key={`label-${e.key}`} type="button" data-map-arc={e.key} aria-pressed={e.key === selected}
                  aria-label={`${systemName(e.from)} to ${systemName(e.to)}: ${e.items.map((i) => i.process).join(', ')}`}
                  title={l.variant > 0 ? l.full : undefined}
                  className={cx('sysm-pill', fail && 'fail', e.key === selected && 'selected', lit?.has(e.key) && 'lit')}
                  style={{ left: l.x, top: l.y, width: l.w, height: LABEL_H, opacity: edgeOpacity(e) }}
                  onMouseEnter={() => setHover(e.key)} onMouseLeave={() => setHover(null)} onFocus={() => setHover(e.key)} onBlur={() => setHover(null)}
                  onClick={(ev) => { ev.stopPropagation(); pick(e.key); }}>
                  {l.name && <span className="sysm-pill-name">{l.name}</span>}
                  {l.name && <span className="sysm-pill-sep"> · </span>}
                  <span className="sysm-pill-num">{l.num}</span>
                </button>
              );
            })}
            {hov && <ArcPeek arc={hov} width={map.width} height={map.height} onEnter={keepHover} onLeave={() => setHover(null)}
              onOpen={(p) => { setHoverNow(null); if (onOpenProcess) onOpenProcess(p); else pick(hov.key); }} />}
          </div>
        )}
      </div>
      {sel && <ArcDetail arc={sel} onClose={() => onSelect(null)} onSearch={onSearch} onOpenSystem={onOpenSystem} onOpenProcess={onOpenProcess} />}
      {!sel && <p className="m-0 text-micro text-n7">Point at a line for its processes, click it to keep them open; click a system to open {openWhat}.</p>}
    </div>
  );
}

/** A system as a card: its mark, its name, its code and what has no line (one-sided processes), what is failing. */
function NodeCard({ n, lone, openWhat, dim, onOpen, onHover }: { n: SysNode; lone: Integration[]; openWhat: string; dim: boolean; onOpen: () => void; onHover: (on: boolean) => void }) {
  const loose = n.part === 'loose';
  const name = systemName(n.code);
  return (
    <button type="button" data-map-system={n.code} onClick={onOpen} title={`${name} (${n.code})${lone.length ? ` · ${lone.length} more ${lone.length === 1 ? 'process' : 'processes'} without a line` : ''}: open ${openWhat}`}
      onPointerEnter={() => onHover(true)} onPointerLeave={() => onHover(false)} onFocus={() => onHover(true)} onBlur={() => onHover(false)}
      className={cx('mvm-node sysm-node', loose && 'loose', n.failing > 0 && 'failing')}
      style={{ left: n.x, top: n.y, width: n.w, height: n.h, '--mvm-dim': dim ? 0.4 : 1 } as CSSProperties}>
      <SystemMark code={n.code} color={systemColor(n.code)} icon={systemIcon(n.code)} logo={systemLogo(n.code)} size={loose ? 24 : 30} className={loose ? 'rounded-[7px]' : 'rounded-[9px]'} />
      <span className="sysm-node-text">
        <span className="sysm-node-name">{name}</span>
        <span className="mvm-node-detail sysm-node-meta">
          <span className="mvm-dim">{n.code}{n.failing === 0 && lone.length > 0 && n.w >= 200 && <> · {lone.length} more {lone.length === 1 ? 'process' : 'processes'}</>}</span>
          {n.failing > 0 && <span className="fail"> · ✕ {n.failing}{n.w >= 200 ? ' failing' : ''}</span>}
        </span>
      </span>
    </button>
  );
}

/** The processes of a line, beside its label while pointed at. */
function ArcPeek({ arc, width, height, onEnter, onLeave, onOpen }: {
  arc: SysEdge; width: number; height: number; onEnter: () => void; onLeave: () => void; onOpen: (process: string) => void;
}) {
  const rows = arc.items.length + 2;
  const h = 24 + rows * 22;
  const below = arc.label.y + LABEL_H + 8 + h <= height || arc.label.y < h + 8;
  const left = Math.max(4, Math.min(width - PEEK_W - 4, arc.label.x + arc.label.w / 2 - PEEK_W / 2));
  return (
    <div role="dialog" aria-label="Processes of this line" onPointerEnter={onEnter} onPointerLeave={onLeave}
      className="absolute z-10 rounded-card bg-n2 shadow-lg ring-1 ring-n4 p-3 flex flex-col gap-1.5"
      style={{ left, width: PEEK_W, ...(below ? { top: arc.label.y + LABEL_H + 8 } : { bottom: height - arc.label.y + 8 }) }}>
      <span className="text-micro text-n7">{systemName(arc.from)} → {systemName(arc.to)} · {pairTitle(arc)}</span>
      {arc.items.map((i) => (
        <button key={i.process} type="button" onClick={() => onOpen(i.process)} title={i.process} data-peek-process={i.process}
          className="flex items-center gap-2 text-small min-w-0 -mx-1.5 px-1.5 py-0.5 rounded-chip bg-transparent border-0 cursor-pointer text-left hover:bg-n3 focus-visible:outline-2 focus-visible:outline-accent">
          <StatusDot status={i.status} />
          <span className={cx('truncate', i.status === 'failing' ? 'text-fail' : 'text-n10')}>{i.process}</span>
          <span className="ml-auto text-micro text-n7 tabular-nums whitespace-nowrap">{count(i.runs)}</span>
        </button>
      ))}
      <span className="text-micro text-n7 truncate">{[...new Set(arc.items.flatMap((i) => i.dataModels))].map((c) => cap(noun(c, 2))).join(' · ')}</span>
    </div>
  );
}

/** The processes along the selected arc with their data models, under the map. */
function ArcDetail({ arc, onClose, onSearch, onOpenSystem, onOpenProcess }: { arc: SysEdge; onClose: () => void; onSearch: (p: string) => void; onOpenSystem: (code: string) => void; onOpenProcess?: (p: string) => void }) {
  return (
    <section aria-label="Selected integration" className="rounded-card bg-n1 ring-1 ring-inset ring-n4 p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <button type="button" className="bg-transparent border-0 p-0 cursor-pointer text-body font-semibold text-n10 hover:underline" onClick={() => onOpenSystem(arc.from)}>{systemName(arc.from)}</button>
        <span className="text-n7" aria-hidden="true">→</span>
        <button type="button" className="bg-transparent border-0 p-0 cursor-pointer text-body font-semibold text-n10 hover:underline" onClick={() => onOpenSystem(arc.to)}>{systemName(arc.to)}</button>
        <span className="text-small text-n7">· {arc.items.length === 1 ? '1 process' : `${count(arc.items.length)} processes`}</span>
        <IconButton label="Close" onClick={onClose} className="ml-auto"><CloseIcon size={16} /></IconButton>
      </div>
      <ul className="m-0 p-0 list-none flex flex-col gap-2.5">
        {arc.items.map((i) => (
          <li key={i.process} className={cx('flex flex-col gap-1 rounded-chip px-3 py-2', i.status === 'failing' ? 'bg-fail-soft' : 'bg-n2')}>
            <span className="flex items-center gap-2 min-w-0 text-small">
              <StatusDot status={i.status} />
              {onOpenProcess ? (
                <button type="button" title={i.process} onClick={() => onOpenProcess(i.process)} data-open-process={i.process}
                  className={cx('min-w-0 truncate text-left bg-transparent border-0 p-0 cursor-pointer font-[inherit] font-medium hover:underline focus-visible:outline-2 focus-visible:outline-accent rounded-[3px]', i.status === 'failing' ? 'text-fail' : 'text-n10')}>{i.process}</button>
              ) : <ProcessMenu process={i.process} onSearch={onSearch} className={cx('font-medium', i.status === 'failing' ? 'text-fail' : 'text-n10')}>{i.process}</ProcessMenu>}
              <span className="ml-auto text-micro text-n7 tabular-nums whitespace-nowrap">{STATUS_TEXT[i.status]}</span>
            </span>
            <span className="text-micro text-n8 tabular-nums">
              {count(i.runs)} {i.runs === 1 ? 'run' : 'runs'} · {count(i.documents)} documents
              {i.failed > 0 && <span className="text-fail"> · {count(i.failed)} with failures</span>}
              {i.lastRun && <> · last {ago(i.lastRun)}</>}
              {cadenceLabel(i.cadenceMs) && <> · {cadenceLabel(i.cadenceMs)}</>}
            </span>
            {i.dataModels.length > 0 && (
              <span className="flex items-center gap-1.5 flex-wrap">
                {i.dataModels.map((c) => <span key={c} title={c} className="inline-flex items-center h-5 px-2 rounded-full bg-n3 text-micro text-n9">{cap(noun(c, 2))}</span>)}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
